import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { NavController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, from, fromEvent, merge, of, pipe, timer } from 'rxjs';
import { catchError, delay, exhaustMap, filter, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { LogService } from '~gc/shared/services/log.service';
import { ensureChildrenExist, ensureExists } from '~gc/shared/utils/rxjs';
import { AppUIService } from '../../app-ui.service';
import { currentRouteUrl } from '../../router.selectors';
import { AppState } from '../../state';
import { appInitialize } from '../app.actions';
import {
  accessTokenNearingExpiration,
  authenticationCompleted,
  authSessionExpired,
  authSessionRefreshed,
  authSessionRefreshFailed,
  claimsAndTokenRetrieved,
  expiredAccessTokenFound,
  offlineTokenFound,
  queueAuthRequest,
  unauthorizedUserIdentified,
  validAccessTokenFound,
} from './auth-connect.actions';
import { AuthConnectService } from './auth-connect.service';
import { claimsInvalidated, loggedOut, login, logout, tokenRefreshBeforeExpirationInitiated } from './auth.actions';
import { authenticatedRole, authenticatedTokenExpiration, hasToken, isAuthenticated } from './auth.selectors';
import { Claims } from './claims.model';

const TAGS = ['Effects', 'Auth', 'Web'];
const CROSS_APP_LOGOUT_COOKIE = 'gc_cross_app_logout_at';
const CROSS_APP_LOGOUT_SEEN_KEY = 'gc_cross_app_logout_seen_at';

const AUTH_CALLBACK_DELAY = 250;

@Injectable()
export class AuthConnectEffects {
  // ALERT: This function (getClaimsAndToken) MUST be at the TOP of this class to prevent build errors!!
  getClaimsAndToken = () =>
    pipe(
      switchMap(() => from(this.auth.getIdToken())),
      switchMap(claims =>
        from(this.auth.getAccessToken()).pipe(
          map(
            token =>
              ({
                claims,
                token,
              }) as { claims?: Claims; token?: string },
          ),
        ),
      ),
    );

  constructor(
    private actions$: Actions,
    private appUI: AppUIService,
    private auth: AuthConnectService,
    private location: Location,
    private nav: NavController,
    private log: LogService,
    private store: Store<AppState>,
  ) {}

  // NOTE: When the app starts, not logging out, try to restore the prior authenticated user...
  restoreAuthentication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appInitialize),
      map(() => this.location.path()),
      filter(url => !url.endsWith('/logout')),
      switchMap(() => this.auth.isAccessTokenAvailable()),
      filter(hasToken => !!hasToken),
      switchMap(() => this.auth.isAccessTokenExpired()),
      map(isExpired => (isExpired ? expiredAccessTokenFound() : validAccessTokenFound())),
    ),
  );

  // NOTE: On web, when no local token exists, try redirect-based Silent Auth (full redirect
  // to Auth0 with prompt=none). This allows SSO across apps on the same Auth0 tenant.
  trySilentAuthOnLoad$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appInitialize),
        map(() => this.location.path()),
        filter(url => !url.endsWith('/login-success') && !url.endsWith('/logout')),
        filter(() => !Capacitor.isNativePlatform()),
        switchMap(() => from(this.auth.isAccessTokenAvailable())),
        filter(hasToken => !hasToken),
        tap(() => this.log.debug(TAGS, 'No local token; attempting silent auth redirect.')),
        switchMap(() => from(this.auth.trySilentAuthRedirect())),
      ),
    { dispatch: false },
  );

  getAuthenticationTokenAndClaims$ = createEffect(() =>
    this.actions$.pipe(
      ofType(validAccessTokenFound, offlineTokenFound, authenticationCompleted, authSessionRefreshed),
      tap(({ type }) =>
        this.log.warn(
          TAGS,
          type === authenticationCompleted.type ? 'Authentication succeeded' : 'Already authenticated!',
        ),
      ),
      switchMap(action =>
        action.type === authenticationCompleted.type ? of(action).pipe(delay(AUTH_CALLBACK_DELAY)) : of(action),
      ),
      this.getClaimsAndToken(),
      ensureChildrenExist(value => !!value?.claims && !!value.token),
      tap(() => this.syncSeenCrossAppLogoutMarker()),
      map(claimsAndToken => claimsAndTokenRetrieved(claimsAndToken)),
    ),
  );

  refreshSessionOnExpiredTokenFound$ = createEffect(() =>
    this.actions$.pipe(
      ofType(expiredAccessTokenFound, accessTokenNearingExpiration),
      filter(() => navigator.onLine),
      switchMap(() =>
        from(this.auth.refreshSession()).pipe(
          map(() => authSessionRefreshed()),
          catchError(() => of(authSessionRefreshFailed())),
        ),
      ),
    ),
  );

  returnToLoginScreenOnExpiredTokenFound$ = createEffect(() =>
    this.actions$.pipe(
      ofType(expiredAccessTokenFound, accessTokenNearingExpiration),
      filter(() => !navigator.onLine),
      switchMap(() => this.auth.getRefreshToken()),
      map(token => (!!token ? offlineTokenFound() : unauthorizedUserIdentified())),
    ),
  );

  returnToLoginPageOnUnauthorized$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(unauthorizedUserIdentified, authSessionRefreshFailed),
        tap(() => this.auth.clearStorage()),
        withLatestFrom(this.store.select(currentRouteUrl)),
        filter(url => url.includes('/app')),
        tap(() => this.nav.navigateRoot('/login')),
      ),
    { dispatch: false },
  );

  launchApp$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(claimsAndTokenRetrieved),
        tap(() => this.log.debug(TAGS, 'Authorization token and claims stored, redirecting to app...')),
        withLatestFrom(this.store.select(authenticatedRole), this.store.select(currentRouteUrl)),
        switchMap(([, role, route]) =>
          !!role
            ? route?.includes('/app/')
              ? of(null)
              : this.nav.navigateRoot('/app/dashboard')
            : this.nav.navigateRoot('/login-landing'),
        ),
      ),
    { dispatch: false },
  );

  checkShouldRefreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(claimsAndTokenRetrieved, claimsInvalidated),
      withLatestFrom(this.store.select(hasToken)),
      filter(([, hasToken]) => hasToken),
      map(() => tokenRefreshBeforeExpirationInitiated()),
    ),
  );

  refreshTokenBeforeExpiration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tokenRefreshBeforeExpirationInitiated),
      withLatestFrom(this.store.select(authenticatedTokenExpiration)),
      map(([, expiration]) => expiration),
      ensureExists(expiration => !!expiration),
      map(expiration => new Date((expiration - 60) * 1000)),
      tap(exp => this.log.debug(TAGS, `Creating expiration event timer with date: ${exp.toISOString()}`)),
      switchMap(expirationDate => timer(expirationDate).pipe(takeUntil(this.actions$.pipe(ofType(logout))))),
      map(() => accessTokenNearingExpiration()),
    ),
  );

  // NOTE: This effect will ultimately result in a redirection to auth0's /authenticate page,
  // AWAY from the app...
  logIn$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(login),
        tap(() => this.log.debug(TAGS, 'Logging in...')),
        switchMap(() => this.auth.login()),
      ),
    { dispatch: false },
  );

  logOut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logout),
      tap(() => this.log.debug(TAGS, 'Logging out...')),
      tap(() => this.markCrossAppLogout()),
      tap(() => this.auth.clearStorage()),
      map(() => loggedOut()),
    ),
  );

  syncCrossAppLogout$ = createEffect(() =>
    merge(
      this.actions$.pipe(ofType(appInitialize), map(() => null)),
      typeof window !== 'undefined' ? fromEvent(window, 'focus').pipe(map(() => null)) : EMPTY,
      typeof document !== 'undefined'
        ? fromEvent(document, 'visibilitychange').pipe(
            filter(() => !document.hidden),
            map(() => null),
          )
        : EMPTY,
    ).pipe(
      withLatestFrom(this.store.select(isAuthenticated)),
      filter(([, authenticated]) => authenticated),
      filter(() => this.hasNewCrossAppLogoutMarker()),
      tap(() => this.log.debug(TAGS, 'Detected cross-app logout marker, logging out.')),
      map(() => logout()),
    ),
  );

  logOutAuthProvider$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loggedOut),
        tap(() => this.log.debug(TAGS, 'Logged out.')),
        switchMap(() => this.auth.logout()),
      ),
    { dispatch: false },
  );

  // NOTE: This effect will handle the redirection back to the app from auth0...
  completeAuthentication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appInitialize),
      map(() => this.location.path()),
      filter(url => url.endsWith('/login-success')),
      tap(() => this.log.info(TAGS, 'Completing authentication process...')),
      exhaustMap(() => this.auth.handleLoginCallback()),
      map(() => authenticationCompleted()),
    ),
  );

  completeQueuedAuthentication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(queueAuthRequest),
      tap(() => this.log.info(TAGS, 'Completing authentication process...')),
      exhaustMap(() => this.auth.handleLoginCallback()),
      map(() => authenticationCompleted()),
    ),
  );

  completeLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(queueAuthRequest, appInitialize),
      map(() => this.location.path()),
      filter(url => url.endsWith('/logout')),
      tap(() => this.log.info(TAGS, 'Completing logout process...')),
      exhaustMap(() => from(this.auth.handleLogoutCallback())),
      tap(() => this.nav.navigateRoot('/login')),
      map(() => loggedOut()),
    ),
  );

  handleSessionExpired$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authSessionExpired),
      exhaustMap(() =>
        from(
          (console.warn('Attempting Refresh of user session after unauthorized request'), this.auth.refreshSession()),
        ).pipe(
          map(() => false),
          tap({
            error: err =>
              console.warn('Error Refreshing Session After Unauthorized Request.  Prompting User To Login.'),
          }),
          catchError(err =>
            from(
              this.appUI.alert(
                'Session Expired',
                `You're session has expired. You'll have to login to continue.`,
                true,
              ),
            ).pipe(map(() => true)),
          ),
        ),
      ),
      filter(requiresLogin => requiresLogin),
      map(() => login()),
    ),
  );

  private markCrossAppLogout(): void {
    if (typeof document === 'undefined') {
      return;
    }
    const timestamp = Date.now();
    document.cookie = `${CROSS_APP_LOGOUT_COOKIE}=${timestamp}; path=/; SameSite=Lax`;
    this.setSeenCrossAppLogoutMarker(timestamp);
  }

  private hasNewCrossAppLogoutMarker(): boolean {
    const marker = this.getCrossAppLogoutMarker();
    if (!marker) {
      return false;
    }
    const seen = this.getSeenCrossAppLogoutMarker();
    if (marker > seen) {
      this.setSeenCrossAppLogoutMarker(marker);
      return true;
    }
    return false;
  }

  private syncSeenCrossAppLogoutMarker(): void {
    const marker = this.getCrossAppLogoutMarker();
    if (marker) {
      this.setSeenCrossAppLogoutMarker(marker);
    }
  }

  private getCrossAppLogoutMarker(): number {
    if (typeof document === 'undefined') {
      return 0;
    }
    const cookie = document.cookie
      .split(';')
      .map(entry => entry.trim())
      .find(entry => entry.startsWith(`${CROSS_APP_LOGOUT_COOKIE}=`));
    if (!cookie) {
      return 0;
    }
    const value = Number(cookie.split('=')[1]);
    return Number.isFinite(value) ? value : 0;
  }

  private getSeenCrossAppLogoutMarker(): number {
    if (typeof sessionStorage === 'undefined') {
      return 0;
    }
    const value = Number(sessionStorage.getItem(CROSS_APP_LOGOUT_SEEN_KEY));
    return Number.isFinite(value) ? value : 0;
  }

  private setSeenCrossAppLogoutMarker(value: number): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    sessionStorage.setItem(CROSS_APP_LOGOUT_SEEN_KEY, String(value));
  }
}
